import { api } from '@/lib/api';

const SW_PATH = '/sw.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH);
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.error('SW registration failed', e);
    return null;
  }
}

async function getVapidPublicKey() {
  try {
    const res = await api.get('/push/vapid-public-key');
    return res.data?.public_key || null;
  } catch (e) {
    return null;
  }
}

export async function ensurePushSubscription({ requestPermission = false } = {}) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  if (Notification.permission === 'default') {
    if (!requestPermission) return { ok: false, reason: 'permission-required' };
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: 'denied' };
  }

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'sw-failed' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await getVapidPublicKey();
    if (!publicKey) return { ok: false, reason: 'no-vapid' };
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      console.error('pushManager.subscribe failed', e);
      return { ok: false, reason: 'subscribe-failed' };
    }
  }

  try {
    const json = sub.toJSON();
    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });
    return { ok: true, subscription: json };
  } catch (e) {
    console.error('POST /push/subscribe failed', e);
    return { ok: false, reason: 'server-error' };
  }
}

export async function unsubscribePush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
  } catch (e) { /* ignore */ }
  try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
}
